import { Injectable } from '@angular/core';
import { CaiApi } from '../../../core/utils/cai-api';
import { API_WALLET } from '../../constant/api-wallet.const';
import { TransactionCategoryEnum } from '../../enum/transaction-category.enum';
import { PayToPrefill } from '../../model/payto-prefill.model';
import { IPresignedFile } from '../../model/presigned-file.model';
import { IWalletBlobFile } from '../../model/blob-file.model';
import { ICargoMartUserInfo, ITransactionAttachment } from '../../model';
import { IGetPaidTransaction } from '../../../core/model/get-paid-transaction.model';
import { RecaptchaService } from '../../../core';

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  constructor(private readonly recaptchaService: RecaptchaService) {}

  async getCategories(
    isGuestUser?: boolean,
  ): Promise<TransactionCategoryEnum[]> {
    const requestURL = API_WALLET.URLs.Transaction.Categories;
    const headers = {};
    if (isGuestUser) {
      headers['x-captcha-token'] =
        await this.recaptchaService.getCaptchaToken();
    }

    return CaiApi.get(API_WALLET.name, requestURL, { headers });
  }

  async getPrefill(
    prefillId: string,
    isGuestUser?: boolean,
  ): Promise<PayToPrefill> {
    const requestURL = API_WALLET.URLs.Transaction.PrefillById.replace(
      '{prefillId}',
      prefillId,
    );
    const headers = {};
    if (isGuestUser) {
      headers['x-captcha-token'] =
        await this.recaptchaService.getCaptchaToken();
    }
    return CaiApi.get(API_WALLET.name, requestURL, { headers });
  }

  async fetchGetPaidTransaction(
    transactionId: string,
    isGuestUser?: boolean,
  ): Promise<IGetPaidTransaction> {
    const requestURL = API_WALLET.URLs.Transaction.GetPaid.replace(
      '{transactionId}',
      transactionId,
    );
    const headers = {};
    if (isGuestUser) {
      headers['x-captcha-token'] =
        await this.recaptchaService.getCaptchaToken();
    }
    return CaiApi.get(API_WALLET.name, requestURL, { headers });
  }

  async uploadAttachments(
    fileEntries: IWalletBlobFile[],
    isGuestUser?: boolean,
  ): Promise<IPresignedFile[]> {
    const requestURL = API_WALLET.URLs.Transaction.PostAttachments;
    const extraOptions: any = {
      body: fileEntries,
      headers: {},
    };

    if (isGuestUser) {
      extraOptions.headers['x-captcha-token'] =
        await this.recaptchaService.getCaptchaToken();
    }

    return CaiApi.post(API_WALLET.name, requestURL, extraOptions);
  }

  async getAttachments(
    transactionId: string,
    documentType?: string,
    isGuestUser?: boolean,
  ): Promise<ITransactionAttachment[]> {
    const requestURL = API_WALLET.URLs.Transaction.GetAttachments.replace(
      '{transactionId}',
      transactionId,
    );
    let extraOptions = {};
    if (documentType) {
      extraOptions = { queryParams: { documentType } };
    }
    if (isGuestUser) {
      extraOptions = {
        ...extraOptions,
        headers: {
          'x-captcha-token': await this.recaptchaService.getCaptchaToken(),
        },
      };
    }
    return CaiApi.get(API_WALLET.name, requestURL, extraOptions);
  }

  async approvePaymentRequest(
    transactionId: string,
    body: ICargoMartUserInfo,
  ): Promise<void> {
    const requestURL = API_WALLET.URLs.Transaction.ApprovePayTo.replace(
      '{transactionId}',
      transactionId,
    );

    return CaiApi.put(API_WALLET.name, requestURL, { body });
  }

  async rejectPaymentRequest(
    transactionId: string,
    body: ICargoMartUserInfo,
  ): Promise<void> {
    const requestURL = API_WALLET.URLs.Transaction.RejectPayTo.replace(
      '{transactionId}',
      transactionId,
    );

    return CaiApi.put(API_WALLET.name, requestURL, { body });
  }
}
